import {
	type InfiniteData,
	type UndefinedInitialDataInfiniteOptions,
	type UseInfiniteQueryResult,
	useInfiniteQuery,
} from "@tanstack/react-query";
import { NotificationCenterKeys } from "web_myplann/Api/NotificationCenter/notificationCenterKeys";
import { useRemindersRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import {
	getNotificationCenterRecentV2,
	type RecentTimelineItemTypeFilterEnum,
	type TimelineResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponseQuery } from "web_myplann/Infrastructure/Api/ApiUtils";
import { filterReminderItemsByRight } from "web_myplann/NotificationCenter/Utils/filterReminderItemsByRight";

const STALE_TIME_MS = 60_000;
const PAGE_LIMIT = 20;

type Key = ReturnType<typeof NotificationCenterKeys.recent>;

interface RecentTimelineParams {
	type: RecentTimelineItemTypeFilterEnum;
}

const getRecentInfiniteOptions = (
	type: RecentTimelineItemTypeFilterEnum,
	hasRemindersRight: boolean,
): UndefinedInitialDataInfiniteOptions<
	TimelineResponseDTO,
	Error,
	InfiniteData<TimelineResponseDTO, number>,
	Key,
	number
> => ({
	queryKey: NotificationCenterKeys.recent(type),
	queryFn: (ctx) =>
		processResponseQuery(() =>
			getNotificationCenterRecentV2(
				type,
				undefined,
				undefined,
				undefined,
				PAGE_LIMIT,
				ctx.pageParam,
			),
		)(ctx),
	getNextPageParam: (lastPage, allPages) => {
		if (!lastPage.meta.hasMore) {
			return null;
		}
		return allPages.length * PAGE_LIMIT;
	},
	initialPageParam: 0,
	staleTime: STALE_TIME_MS,
	select: (data) => ({
		...data,
		pages: data.pages.map((page) =>
			filterReminderItemsByRight(page, hasRemindersRight),
		),
	}),
});

export const useRecentTimelineInfiniteQuery = (
	params: RecentTimelineParams,
): UseInfiniteQueryResult<InfiniteData<TimelineResponseDTO, number>, Error> => {
	const hasRemindersRight = useRemindersRightOptional();

	return useInfiniteQuery(
		getRecentInfiniteOptions(params.type, hasRemindersRight),
	);
};
